import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';

export interface ScreenHeaderProps {
  /** Sentence-case lead-in above the title, set in `text-kicker`. */
  kicker?: string;
  /** The page's one real `<h1>`. */
  title: string;
  /** A line under the title (e.g. "12 titles"), `text-subhead`. */
  meta?: ReactNode;
  /** Right-aligned controls beside the title block on `sm+`, wrapping below on mobile. */
  actions?: ReactNode;
  /** A quiet back link rendered above the kicker. */
  back?: { to: string; label: string };
  /**
   * Optional replacement for the title text (e.g. a skeleton). The `<h1>`
   * still renders; `title` is used as its `aria-label` so the accessible name
   * survives the placeholder.
   */
  titleNode?: ReactNode;
  className?: string;
}

/**
 * The non-collapsing screen header (ui-ux.md §3): a sentence-case `text-kicker`
 * + Bricolage `text-display`/`text-display-sm` heading pair on the plain
 * canvas — no glow, no gradient, no border — plus optional meta, back
 * link, and right-aligned actions. `<Masthead>` reuses this for its header
 * block; the ~8 screens that hand-roll this same header should adopt it too.
 */
export function ScreenHeader({
  kicker,
  title,
  meta,
  actions,
  back,
  titleNode,
  className,
}: ScreenHeaderProps) {
  return (
    // Callers sit in a `gap-8` column: no bottom padding on mobile (the gap is
    // the 32 rhythm), +16 on sm+ for the 48 desktop rhythm (ui-ux.md §2).
    <header className={cn('pt-2 sm:pb-4', className)}>
      {back ? (
        <Link
          to={back.to}
          className="touch-target-min mb-2 inline-flex items-center gap-1 text-subhead text-ink-secondary hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden />
          {back.label}
        </Link>
      ) : null}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {kicker ? <p className="text-kicker text-ink-secondary">{kicker}</p> : null}
          <h1
            className="text-display-sm text-ink sm:text-display"
            aria-label={titleNode ? title : undefined}
          >
            {titleNode ?? title}
          </h1>
          {meta ? <div className="text-subhead text-ink-secondary">{meta}</div> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}
